// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  partner/src/pages/Dashboard.jsx
//  Copy to: C:\quickry\partner\src\pages\Dashboard.jsx
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  CheckCircle2, Clock, XCircle, Eye, Zap, Phone,
  Mail, MapPin, RefreshCw, LogOut, Building2,
  FileText, CreditCard, AlertCircle, ChevronRight,
  TrendingUp, Star, ShoppingBag,
} from "lucide-react";

const C = {
  orange:"#f97316", orangeDark:"#ea580c", orangeLight:"rgba(249,115,22,0.1)",
  black:"#0a0a0a", dark:"rgba(255,255,255,0.04)", white:"#ffffff",
  border:"rgba(255,255,255,0.08)", card:"rgba(255,255,255,0.04)",
  text:"rgba(255,255,255,0.9)", textSub:"rgba(255,255,255,0.55)", textMuted:"rgba(255,255,255,0.35)",
  green:"#4ade80", greenBg:"rgba(74,222,128,0.1)", greenBorder:"rgba(74,222,128,0.2)",
  red:"#f87171", redBg:"rgba(248,113,113,0.1)",
  yellow:"#fbbf24", yellowBg:"rgba(251,191,36,0.1)",
  blue:"#60a5fa", blueBg:"rgba(96,165,250,0.1)",
};

const STATUS_CONFIG = {
  pending: {
    label:"Application Received",
    color:C.yellow, bg:C.yellowBg, border:"rgba(251,191,36,0.2)",
    icon:Clock,
    message:"Aapki application humein mil gayi! Humari team review kar rahi hai.",
    eta:"24-48 ghante mein update milega",
  },
  under_review: {
    label:"Under Review",
    color:C.blue, bg:C.blueBg, border:"rgba(96,165,250,0.2)",
    icon:Eye,
    message:"Documents verify ho rahe hain. Jaldi hi call aayegi!",
    eta:"24 ghante mein response expected",
  },
  approved: {
    label:"Approved! 🎉",
    color:C.green, bg:C.greenBg, border:C.greenBorder,
    icon:CheckCircle2,
    message:"Congratulations! Aapka business Quickry pe live hai.",
    eta:"Orders abhi shuru ho sakte hain!",
  },
  rejected: {
    label:"Application Rejected",
    color:C.red, bg:C.redBg, border:"rgba(248,113,113,0.2)",
    icon:XCircle,
    message:"Kuch issues hain. Reason dekho aur reapply karo.",
    eta:"",
  },
};

const TIMELINE = [
  { key:"received",    label:"Application Received",   sub:"Form submitted successfully"        },
  { key:"review",      label:"Document Verification",  sub:"Team reviewing your documents"       },
  { key:"onboarding",  label:"Onboarding Call",        sub:"Team will call you for setup"        },
  { key:"live",        label:"Go Live on Quickry!",    sub:"Your business visible to customers"  },
];

export default function Dashboard() {
  const navigate          = useNavigate();
  const { vendor, logout, getToken } = useAuth();

  const [application, setApplication] = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [refreshing,  setRefreshing]  = useState(false);

  const fetchApplication = async (showRefresh=false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const API = typeof window !== 'undefined' && window.location.hostname !== 'localhost' ? 'https://quickry-ivrv.onrender.com/api' : 'http://localhost:5000/api';
      const token = getToken();

      // ✅ FIX 1: /vendors/me — token se vendor data milega (most accurate)
      if (token) {
        const meRes  = await fetch(`${API}/vendors/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const meData = await meRes.json();
        if (meData.success && meData.data) {
          setApplication(meData.data);
          setLoading(false);
          setRefreshing(false);
          return;
        }
      }

      // ✅ FIX 2: Fallback — email se dhundo (multiple sources check karo)
      const email = vendor?.email
        || vendor?.vendor?.email
        || vendor?.data?.email
        || "";

      if (email) {
        const res  = await fetch(`${API}/vendors/status?email=${encodeURIComponent(email)}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await res.json();
        if (data.success && data.data) {
          setApplication(data.data);
        }
      }
    } catch(e) {
      console.error(e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchApplication(); }, []);

  const handleLogout = () => { logout(); navigate("/"); };

  const status = application?.status || "pending";
  const cfg    = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const StatusIcon = cfg.icon;

  // Timeline active step
  const timelineStep = { pending:0, under_review:1, approved:3, rejected:1 }[status] ?? 0;

  return (
    <div style={{ minHeight:"100vh", background:C.black, color:C.white, fontFamily:"'Plus Jakarta Sans','DM Sans',sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,800;9..144,900&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}@keyframes fadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}@keyframes spin{to{transform:rotate(360deg)}}@keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}.fu{animation:fadeUp .4s ease both}.spin{animation:spin 1s linear infinite}.pulse{animation:pulse 2s infinite}`}</style>

      {/* ── NAV ── */}
      <nav style={{ borderBottom:`1px solid ${C.border}`, padding:"0 5%", background:"rgba(10,10,10,0.9)", backdropFilter:"blur(20px)", position:"sticky", top:0, zIndex:100 }}>
        <div style={{ maxWidth:960, margin:"0 auto", height:62, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.3rem", letterSpacing:"-1px" }}>
            Quick<span style={{ color:C.orange }}>ry</span>
            <span style={{ fontSize:".6rem", color:C.textMuted, fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:400, marginLeft:6 }}>Partner</span>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:14 }}>
            <div style={{ fontSize:".8rem", color:C.textSub }}>
              {vendor?.name || vendor?.email || "Partner"}
            </div>
            <button onClick={handleLogout}
              style={{ background:"none", border:`1px solid ${C.border}`, borderRadius:9, padding:"7px 14px", color:C.textSub, fontSize:".78rem", fontWeight:600, cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", gap:6, transition:"all .2s" }}
              onMouseEnter={e=>{e.currentTarget.style.borderColor="rgba(255,255,255,0.25)";e.currentTarget.style.color="#fff"}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.color=C.textSub}}>
              <LogOut size={14}/> Logout
            </button>
          </div>
        </div>
      </nav>

      <div style={{ maxWidth:960, margin:"0 auto", padding:"32px 5% 60px" }}>

        {loading ? (
          <div style={{ textAlign:"center", padding:60, color:C.textSub }}>
            <RefreshCw size={28} className="spin" style={{ color:C.orange, marginBottom:12 }}/>
            <div>Loading your application...</div>
          </div>
        ) : !application ? (
          /* No application yet */
          <div className="fu" style={{ textAlign:"center", padding:"60px 20px" }}>
            <div style={{ width:72, height:72, borderRadius:"50%", background:C.orangeLight, border:"1.5px solid rgba(249,115,22,0.3)", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 20px" }}>
              <FileText size={32} color={C.orange}/>
            </div>
            <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.5rem", letterSpacing:"-0.8px", marginBottom:10 }}>Application nahi mili</div>
            <div style={{ color:C.textSub, fontSize:".88rem", marginBottom:28, lineHeight:1.7 }}>
              Aapne abhi tak application submit nahi ki.<br/>Abhi shuru karo!
            </div>
            <button onClick={()=>navigate("/onboarding")}
              style={{ background:`linear-gradient(135deg,${C.orangeDark},${C.orange})`, border:"none", borderRadius:14, padding:"14px 32px", color:"#fff", fontWeight:700, fontSize:".95rem", cursor:"pointer", fontFamily:"inherit", boxShadow:"0 6px 20px rgba(249,115,22,0.3)", display:"inline-flex", alignItems:"center", gap:8 }}>
              Start Application <ChevronRight size={16}/>
            </button>
          </div>
        ) : (
          <>
            {/* Welcome */}
            <div className="fu" style={{ marginBottom:24 }}>
              <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.4rem", letterSpacing:"-0.8px", marginBottom:4 }}>
                Welcome, {application.ownerName?.split(" ")[0] || "Partner"}! 👋
              </div>
              <div style={{ fontSize:".82rem", color:C.textSub }}>
                {application.businessName} · {application.area}, Akola
              </div>
            </div>

            {/* Status Card */}
            <div className="fu" style={{ background:cfg.bg, border:`1.5px solid ${cfg.border}`, borderRadius:20, padding:"24px", marginBottom:20 }}>
              <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:16, flexWrap:"wrap" }}>
                <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                  <div style={{ width:52, height:52, borderRadius:16, background:`${cfg.color}15`, border:`1px solid ${cfg.color}30`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    <StatusIcon size={24} color={cfg.color}/>
                  </div>
                  <div>
                    <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:800, fontSize:"1.1rem", color:cfg.color, marginBottom:4 }}>{cfg.label}</div>
                    <div style={{ fontSize:".82rem", color:C.textSub, lineHeight:1.5 }}>{cfg.message}</div>
                    {cfg.eta && <div style={{ fontSize:".75rem", color:cfg.color, marginTop:6, fontWeight:600 }}>⏱ {cfg.eta}</div>}
                  </div>
                </div>
                <button onClick={()=>fetchApplication(true)}
                  style={{ background:"rgba(255,255,255,0.06)", border:`1px solid ${C.border}`, borderRadius:10, padding:"8px 14px", fontSize:".75rem", fontWeight:600, color:C.textSub, cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", gap:6, flexShrink:0 }}>
                  <RefreshCw size={13} style={refreshing?{animation:"spin 1s linear infinite"}:{}}/> Refresh
                </button>
              </div>

              {/* Rejection reason */}
              {status==="rejected" && application.rejectionReason && (
                <div style={{ background:"rgba(248,113,113,0.08)", border:"1px solid rgba(248,113,113,0.2)", borderRadius:12, padding:"12px 14px", marginTop:16, fontSize:".8rem", color:"#f87171" }}>
                  <strong>Rejection Reason:</strong> {application.rejectionReason}
                </div>
              )}
            </div>

            {/* Timeline */}
            <div className="fu" style={{ animationDelay:".1s", background:C.card, border:`1px solid ${C.border}`, borderRadius:20, padding:"20px 24px", marginBottom:20 }}>
              <div style={{ fontWeight:700, fontSize:".72rem", color:C.textMuted, letterSpacing:"1px", textTransform:"uppercase", marginBottom:20 }}>Application Progress</div>
              {TIMELINE.map((t, i) => {
                const done   = i <= timelineStep && status !== "rejected";
                const active = i === timelineStep + 1 && status !== "approved";
                const rej    = status === "rejected" && i === 1;
                return (
                  <div key={i} style={{ display:"flex", gap:14, alignItems:"flex-start", marginBottom:i<3?20:0 }}>
                    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:0 }}>
                      <div style={{ width:32, height:32, borderRadius:"50%", background:rej?"rgba(248,113,113,0.15)":done?C.greenBg:active?C.orangeLight:"rgba(255,255,255,0.05)", border:`1.5px solid ${rej?"rgba(248,113,113,0.3)":done?C.greenBorder:active?"rgba(249,115,22,0.3)":C.border}`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, transition:"all .4s" }}>
                        {rej
                          ? <XCircle size={14} color={C.red}/>
                          : done
                            ? <CheckCircle2 size={14} color={C.green}/>
                            : active
                              ? <span className="pulse" style={{ width:8, height:8, borderRadius:"50%", background:C.orange, display:"block" }}/>
                              : <span style={{ width:8, height:8, borderRadius:"50%", background:"rgba(255,255,255,0.15)", display:"block" }}/>
                        }
                      </div>
                      {i < 3 && <div style={{ width:1, height:20, background:done?C.greenBorder:C.border, marginTop:4 }}/>}
                    </div>
                    <div style={{ paddingBottom: i<3?0:0, marginTop:4 }}>
                      <div style={{ fontWeight:600, fontSize:".85rem", color:done?C.white:active?C.orange:C.textMuted, transition:"color .3s" }}>{t.label}</div>
                      <div style={{ fontSize:".72rem", color:C.textMuted, marginTop:3 }}>{t.sub}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Business Info */}
            <div className="fu" style={{ animationDelay:".2s", background:C.card, border:`1px solid ${C.border}`, borderRadius:20, padding:"20px 24px", marginBottom:20 }}>
              <div style={{ fontWeight:700, fontSize:".72rem", color:C.textMuted, letterSpacing:"1px", textTransform:"uppercase", marginBottom:16 }}>Your Business</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
                {[
                  { icon:Building2, label:"Business Name",  val:application.businessName  },
                  { icon:FileText,  label:"Business Type",  val:application.businessType?.replace("_"," ").replace(/\b\w/g,l=>l.toUpperCase()) },
                  { icon:Phone,     label:"Phone",          val:application.phone         },
                  { icon:Mail,      label:"Email",          val:application.email         },
                  { icon:MapPin,    label:"Area",           val:`${application.area}, ${application.city||"Akola"}` },
                ].map((r,i) => (
                  <div key={i} style={{ display:"flex", gap:10, alignItems:"flex-start" }}>
                    <div style={{ width:30, height:30, borderRadius:9, background:"rgba(255,255,255,0.06)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                      <r.icon size={14} color={C.textSub}/>
                    </div>
                    <div>
                      <div style={{ fontSize:".62rem", color:C.textMuted, fontWeight:600, letterSpacing:".3px", textTransform:"uppercase", marginBottom:2 }}>{r.label}</div>
                      <div style={{ fontSize:".82rem", color:C.text, fontWeight:600, wordBreak:"break-all" }}>{r.val||"—"}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Approved — show stats placeholder */}
            {status === "approved" && (
              <div className="fu" style={{ animationDelay:".3s", background:`linear-gradient(135deg,rgba(249,115,22,0.08),rgba(249,115,22,0.04))`, border:"1px solid rgba(249,115,22,0.2)", borderRadius:20, padding:"24px", marginBottom:20 }}>
                <div style={{ fontWeight:700, fontSize:".72rem", color:C.textMuted, letterSpacing:"1px", textTransform:"uppercase", marginBottom:16 }}>Your Stats</div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12 }}>
                  {[
                    { icon:ShoppingBag, val:"0", label:"Total Orders",   color:C.orange },
                    { icon:TrendingUp,  val:"₹0", label:"Total Earnings", color:C.green  },
                    { icon:Star,        val:"—",  label:"Avg Rating",     color:C.yellow },
                  ].map((s,i) => (
                    <div key={i} style={{ background:"rgba(255,255,255,0.04)", borderRadius:14, padding:"16px 14px", textAlign:"center" }}>
                      <s.icon size={20} color={s.color} style={{ marginBottom:8 }}/>
                      <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.3rem", color:s.color }}>{s.val}</div>
                      <div style={{ fontSize:".65rem", color:C.textMuted, marginTop:3 }}>{s.label}</div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop:16, fontSize:".78rem", color:C.textSub, textAlign:"center" }}>
                  Orders aane shuru honge jab customers aapke area mein order karenge 🚀
                </div>
              </div>
            )}

            {/* Rejected — reapply */}
            {status === "rejected" && (
              <button onClick={()=>navigate("/onboarding")}
                style={{ width:"100%", background:`linear-gradient(135deg,${C.orangeDark},${C.orange})`, border:"none", borderRadius:14, padding:"15px", color:"#fff", fontWeight:800, fontSize:"1rem", cursor:"pointer", fontFamily:"inherit", boxShadow:"0 6px 20px rgba(249,115,22,0.3)", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
                Reapply with Updated Documents <ChevronRight size={18}/>
              </button>
            )}

            {/* Contact support */}
            <div className="fu" style={{ animationDelay:".3s", marginTop:20, textAlign:"center" }}>
              <div style={{ fontSize:".78rem", color:C.textMuted }}>
                Questions? WhatsApp karo: <strong style={{ color:C.text }}>+91 8550900165</strong>
                {" · "}Email: <strong style={{ color:C.text }}>partner@quickry.in</strong>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}